import { useState } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────
type AuthMode = 'login' | 'register';
type DashTab = 'overview' | 'videos' | 'withdraw';
type PayMethod = 'wechat' | 'alipay' | 'credit' | 'debit';

// ─── Mock data ────────────────────────────────────────────────────────────────
const kaoyanCourses = [
  { id: 'k1', title: '考研数学·高等数学极限微积分精讲', teacher: '钱数学老师', price: 44.9, originalPrice: 89.9, students: 18430, rating: 4.9, sales: 1240, revenue: 55676, subject: '数学', image: 'https://public.readdy.ai/ai/img_res/e7df003649cd9737c97f644998622f15.jpg' },
  { id: 'k2', title: '考研英语·长难句翻译与写作提分', teacher: '孙英语老师', price: 38.9, originalPrice: 77.9, students: 13670, rating: 4.8, sales: 980, revenue: 38122, subject: '英语', image: 'https://readdy.ai/api/search-image?query=Chinese%20English%20teacher%20teaching%20complex%20sentence%20translation%20for%20graduate%20exam%2C%20modern%20study%20room%2C%20English%20grammar%20books%2C%20professional%20academic%20setting%2C%20bright%20lighting&width=400&height=240&seq=ky002&orientation=landscape' },
  { id: 'k3', title: '考研政治·马原近代史纲要精讲', teacher: '吴政治老师', price: 32.9, originalPrice: 65.9, students: 22340, rating: 4.8, sales: 1560, revenue: 51324, subject: '政治', image: 'https://readdy.ai/api/search-image?query=Chinese%20political%20theory%20teacher%20explaining%20Marxist%20philosophy%20for%20graduate%20entrance%20exam%2C%20university%20classroom%2C%20political%20theory%20books%2C%20serious%20academic%20environment&width=400&height=240&seq=ky003&orientation=landscape' },
  { id: 'k4', title: '考研专业课·经济学微观宏观全套', teacher: '周经济老师', price: 52.9, originalPrice: 105.9, students: 9870, rating: 4.9, sales: 760, revenue: 40204, subject: '专业课', image: 'https://readdy.ai/api/search-image?query=Chinese%20economics%20professor%20teaching%20microeconomics%20for%20graduate%20exam%2C%20university%20lecture%20hall%2C%20economic%20charts%20and%20supply%20demand%20graphs%2C%20authoritative%20academic%20atmosphere&width=400&height=240&seq=ky004&orientation=landscape' },
];

const teacherStats = { totalRevenue: 185326, pendingBalance: 42800, totalSales: 4540, totalCourses: 8 };

// ─── Sub-components ───────────────────────────────────────────────────────────

function PaymentModal({ course, onClose }: { course: typeof kaoyanCourses[0]; onClose: () => void }) {
  const [method, setMethod] = useState<PayMethod>('wechat');
  const [paid, setPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvv: '' });

  const handlePay = () => {
    setLoading(true);
    setTimeout(() => { setLoading(false); setPaid(true); }, 2000);
  };

  if (paid) {
    return (
      <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl w-full max-w-sm p-8 text-center">
          <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-green-600 text-3xl"></i>
          </div>
          <h3 className="font-black text-gray-900 text-lg mb-2">支付成功！</h3>
          <p className="text-gray-500 text-sm mb-1">课程已解锁：{course.title}</p>
          <p className="text-gray-400 text-xs mb-6">可在「我的课程」中随时观看</p>
          <button onClick={onClose} className="w-full py-3 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">
            开始学习
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl">
          <h2 className="font-black text-gray-900">购买课程</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>
        <div className="p-6">
          {/* Course info */}
          <div className="bg-gray-50 rounded-2xl p-4 mb-5 flex gap-3">
            <img src={course.image} alt={course.title} className="w-16 h-12 rounded-xl object-cover object-top flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 line-clamp-2">{course.title}</p>
              <p className="text-xs text-gray-400 mt-1">{course.teacher}</p>
            </div>
          </div>
          <div className="flex items-center justify-between mb-5">
            <span className="text-sm text-gray-600">应付金额</span>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-red-600">¥{course.price}</span>
              <span className="text-gray-300 text-sm line-through">¥{course.originalPrice}</span>
            </div>
          </div>

          {/* Payment methods */}
          <p className="text-xs font-semibold text-gray-600 mb-3">选择支付方式</p>
          <div className="grid grid-cols-2 gap-3 mb-5">
            {([
              { id: 'wechat', label: '微信支付', icon: 'ri-wechat-pay-line', color: 'text-green-600' },
              { id: 'alipay', label: '支付宝', icon: 'ri-alipay-line', color: 'text-blue-500' },
              { id: 'credit', label: '信用卡', icon: 'ri-bank-card-line', color: 'text-orange-500' },
              { id: 'debit', label: '借记卡', icon: 'ri-bank-card-2-line', color: 'text-gray-700' },
            ] as { id: PayMethod; label: string; icon: string; color: string }[]).map(m => (
              <button
                key={m.id}
                onClick={() => setMethod(m.id)}
                className={`flex items-center gap-2 py-3 px-4 rounded-xl border-2 transition-all cursor-pointer ${method === m.id ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <i className={`${m.icon} text-lg ${m.color}`}></i>
                <span className="text-sm font-semibold text-gray-700">{m.label}</span>
              </button>
            ))}
          </div>

          {/* QR code for wechat/alipay */}
          {(method === 'wechat' || method === 'alipay') && (
            <div className="text-center bg-gray-50 rounded-2xl p-6 mb-5">
              <div className="w-32 h-32 bg-white border-2 border-gray-200 rounded-xl mx-auto mb-3 flex items-center justify-center">
                <div className="grid grid-cols-5 gap-0.5">
                  {Array.from({ length: 25 }).map((_, i) => (
                    <div key={i} className={`w-4 h-4 rounded-sm ${Math.random() > 0.5 ? 'bg-gray-900' : 'bg-white'}`}></div>
                  ))}
                </div>
              </div>
              <p className="text-sm font-semibold text-gray-700">
                {method === 'wechat' ? '微信扫码支付' : '支付宝扫码支付'}
              </p>
              <p className="text-xs text-gray-400 mt-1">请在60秒内完成支付</p>
            </div>
          )}

          {/* Card form */}
          {(method === 'credit' || method === 'debit') && (
            <div className="flex flex-col gap-3 mb-5">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                  {method === 'credit' ? '信用卡' : '借记卡'}卡号
                </label>
                <input
                  type="text"
                  value={card.number}
                  onChange={e => setCard(c => ({ ...c, number: e.target.value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim() }))}
                  placeholder="0000 0000 0000 0000"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors font-mono"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">持卡人姓名</label>
                <input
                  type="text"
                  value={card.name}
                  onChange={e => setCard(c => ({ ...c, name: e.target.value }))}
                  placeholder="请输入持卡人姓名"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">有效期</label>
                  <input
                    type="text"
                    value={card.expiry}
                    onChange={e => setCard(c => ({ ...c, expiry: e.target.value }))}
                    placeholder="MM / YY"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">CVV</label>
                  <input
                    type="password"
                    value={card.cvv}
                    onChange={e => setCard(c => ({ ...c, cvv: e.target.value.slice(0, 4) }))}
                    placeholder="3-4位"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          <button
            onClick={handlePay}
            disabled={loading}
            className="w-full py-3.5 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60"
          >
            {loading ? '支付处理中...' : `确认支付 ¥${course.price}`}
          </button>
          <p className="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1">
            <i className="ri-shield-check-line text-green-500"></i>
            支付安全加密保护，放心购买
          </p>
        </div>
      </div>
    </div>
  );
}

function TeacherDashModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<DashTab>('overview');
  const [showUpload, setShowUpload] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState<'wechat' | 'alipay' | 'bank'>('wechat');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleWithdraw = () => {
    setWithdrawSuccess(true);
    setTimeout(() => setWithdrawSuccess(false), 3000);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-red-600 rounded-xl">
              <i className="ri-user-star-line text-white text-lg"></i>
            </div>
            <div>
              <h2 className="font-black text-gray-900">考研教师工作台</h2>
              <p className="text-xs text-gray-400">钱数学老师 · 已认证</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <div className="p-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              { label: '总销售额', value: `¥${teacherStats.totalRevenue.toLocaleString()}`, icon: 'ri-money-cny-circle-line', color: 'text-red-600 bg-red-50' },
              { label: '可提现余额', value: `¥${teacherStats.pendingBalance.toLocaleString()}`, icon: 'ri-wallet-3-line', color: 'text-emerald-600 bg-emerald-50' },
              { label: '总销量', value: teacherStats.totalSales.toLocaleString(), icon: 'ri-shopping-bag-line', color: 'text-amber-600 bg-amber-50' },
              { label: '课程数', value: teacherStats.totalCourses, icon: 'ri-video-line', color: 'text-orange-600 bg-orange-50' },
            ].map(s => (
              <div key={s.label} className="bg-gray-50 rounded-2xl p-4">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${s.color.split(' ')[1]} mb-2`}>
                  <i className={`${s.icon} ${s.color.split(' ')[0]} text-base`}></i>
                </div>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className={`text-lg font-black ${s.color.split(' ')[0]}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-full px-1 py-1 mb-5 w-fit">
            {([
              { id: 'overview', label: '数据概览', icon: 'ri-bar-chart-line' },
              { id: 'videos', label: '我的课程', icon: 'ri-video-line' },
              { id: 'withdraw', label: '提现', icon: 'ri-bank-card-line' },
            ] as { id: DashTab; label: string; icon: string }[]).map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${tab === t.id ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-800'}`}
              >
                <i className={t.icon}></i>{t.label}
              </button>
            ))}
          </div>

          {/* Overview */}
          {tab === 'overview' && (
            <div>
              <h3 className="font-black text-gray-900 mb-4 text-sm">近7天销售趋势</h3>
              <div className="flex items-end gap-2 h-32 mb-6">
                {[820, 1240, 980, 1560, 1120, 1890, 2100].map((v, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-400 hidden md:block">¥{(v / 100).toFixed(0)}k</span>
                    <div className="w-full bg-red-500 rounded-t-lg" style={{ height: `${(v / 2100) * 100}%` }}></div>
                    <span className="text-xs text-gray-400">{['一', '二', '三', '四', '五', '六', '日'][i]}</span>
                  </div>
                ))}
              </div>
              <h3 className="font-black text-gray-900 mb-3 text-sm">课程销售排行</h3>
              <div className="flex flex-col gap-3">
                {kaoyanCourses.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <div className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-black flex-shrink-0 ${i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-white' : 'bg-orange-200 text-orange-700'}`}>{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">{c.title}</p>
                      <p className="text-xs text-gray-400">已售 {c.sales} 份</p>
                    </div>
                    <p className="text-sm font-bold text-red-600 whitespace-nowrap">¥{c.revenue.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Videos */}
          {tab === 'videos' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-black text-gray-900 text-sm">我的考研课程</h3>
                <button
                  onClick={() => setShowUpload(true)}
                  className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-xs font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-upload-cloud-line"></i>上传新课程
                </button>
              </div>

              {uploadSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-green-600"></i>
                  <span className="text-green-700 text-sm font-semibold">课程已提交审核，1-3个工作日内上架！</span>
                </div>
              )}

              {showUpload && (
                <div className="bg-gray-50 rounded-2xl p-5 mb-4 border-2 border-dashed border-gray-200">
                  <h4 className="font-bold text-gray-900 text-sm mb-4">上传新考研课程</h4>
                  <div className="flex flex-col gap-3">
                    <input type="text" placeholder="课程标题（如：考研数学·线性代数精讲）" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-red-400 transition-colors" />
                    <div className="grid grid-cols-2 gap-3">
                      <select className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-red-400 transition-colors bg-white cursor-pointer">
                        <option>数学</option>
                        <option>英语</option>
                        <option>政治</option>
                        <option>专业课</option>
                      </select>
                      <input type="number" placeholder="售价（元）" className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-red-400 transition-colors" />
                    </div>
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-red-300 transition-colors">
                      <i className="ri-video-upload-line text-3xl text-gray-300 mb-2 block"></i>
                      <p className="text-sm text-gray-400">点击上传视频文件</p>
                      <p className="text-xs text-gray-300 mt-1">支持 MP4、MOV，最大 2GB</p>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => setShowUpload(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-semibold rounded-full cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">取消</button>
                      <button onClick={() => { setShowUpload(false); setUploadSuccess(true); setTimeout(() => setUploadSuccess(false), 4000); }} className="flex-1 py-2.5 bg-red-600 text-white text-sm font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">提交审核</button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3">
                {kaoyanCourses.map(c => (
                  <div key={c.id} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <img src={c.image} alt={c.title} className="w-16 h-10 rounded-lg object-cover object-top flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">{c.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5">¥{c.price} · 已售 {c.sales} 份 · 收益 ¥{c.revenue.toLocaleString()}</p>
                    </div>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">销售中</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Withdraw */}
          {tab === 'withdraw' && (
            <div>
              <div className="bg-emerald-50 rounded-2xl p-5 mb-5">
                <p className="text-xs text-emerald-600 mb-1">可提现余额</p>
                <p className="text-3xl font-black text-emerald-700">¥{teacherStats.pendingBalance.toLocaleString()}</p>
                <p className="text-xs text-emerald-500 mt-1">平台收取10%服务费，实际到账 ¥{(teacherStats.pendingBalance * 0.9).toFixed(2)}</p>
              </div>

              {withdrawSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-green-600"></i>
                  <span className="text-green-700 text-sm font-semibold">提现申请已提交！1-3个工作日到账</span>
                </div>
              )}

              <p className="text-sm font-semibold text-gray-700 mb-3">提现方式</p>
              <div className="grid grid-cols-3 gap-3 mb-5">
                {([
                  { id: 'wechat', label: '微信', icon: 'ri-wechat-pay-line', color: 'text-green-600' },
                  { id: 'alipay', label: '支付宝', icon: 'ri-alipay-line', color: 'text-blue-500' },
                  { id: 'bank', label: '银行卡', icon: 'ri-bank-card-line', color: 'text-gray-700' },
                ] as { id: 'wechat' | 'alipay' | 'bank'; label: string; icon: string; color: string }[]).map(m => (
                  <button
                    key={m.id}
                    onClick={() => setWithdrawMethod(m.id)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all cursor-pointer ${withdrawMethod === m.id ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <i className={`${m.icon} text-xl ${m.color}`}></i>
                    <span className="text-xs font-semibold text-gray-700">{m.label}</span>
                  </button>
                ))}
              </div>

              <div className="mb-4">
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">提现金额</label>
                <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                  <span className="text-gray-400 text-sm">¥</span>
                  <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="请输入提现金额" className="flex-1 outline-none text-sm" />
                  <button onClick={() => setWithdrawAmount(String(teacherStats.pendingBalance))} className="text-xs text-red-600 font-semibold cursor-pointer whitespace-nowrap hover:text-red-700">全部提现</button>
                </div>
                <p className="text-xs text-gray-400 mt-1">最低提现 ¥100，1-3个工作日到账</p>
              </div>

              {withdrawMethod === 'bank' && (
                <div className="flex flex-col gap-3 mb-4">
                  <input type="text" placeholder="银行卡号" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors" />
                  <input type="text" placeholder="开户行（如：中国工商银行）" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors" />
                  <input type="text" placeholder="持卡人姓名" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-red-400 transition-colors" />
                </div>
              )}

              <button onClick={handleWithdraw} className="w-full py-3.5 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">
                申请提现
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TeacherAuthModal({ onSuccess }: { onSuccess: () => void }) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', subject: 'math', password: '', school: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => { setLoading(false); onSuccess(); }, 1500);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-sm p-7">
        <div className="text-center mb-5">
          <div className="w-12 h-12 flex items-center justify-center bg-red-600 rounded-2xl mx-auto mb-3">
            <i className="ri-user-star-line text-white text-xl"></i>
          </div>
          <h2 className="font-black text-gray-900 text-lg">考研教师入口</h2>
          <p className="text-gray-400 text-xs mt-1">上传课程，帮助考研学子圆梦</p>
        </div>
        <div className="flex bg-gray-100 rounded-full px-1 py-1 mb-5">
          <button onClick={() => setMode('login')} className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === 'login' ? 'bg-red-600 text-white' : 'text-gray-500'}`}>教师登录</button>
          <button onClick={() => setMode('register')} className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${mode === 'register' ? 'bg-red-600 text-white' : 'text-gray-500'}`}>注册入驻</button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {mode === 'register' && (
            <>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-user-line text-gray-400 text-sm"></i>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="真实姓名" className="flex-1 outline-none text-sm" required />
              </div>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-building-line text-gray-400 text-sm"></i>
                <input type="text" value={form.school} onChange={e => setForm(f => ({ ...f, school: e.target.value }))} placeholder="所在院校（如：北京大学）" className="flex-1 outline-none text-sm" required />
              </div>
              <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
                <i className="ri-apps-line text-gray-400 text-sm"></i>
                <select value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} className="flex-1 outline-none text-sm bg-transparent cursor-pointer">
                  <option value="math">考研数学</option>
                  <option value="english">考研英语</option>
                  <option value="politics">考研政治</option>
                  <option value="major">专业课</option>
                </select>
              </div>
            </>
          )}
          <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
            <i className="ri-phone-line text-gray-400 text-sm"></i>
            <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="手机号" className="flex-1 outline-none text-sm" required />
          </div>
          <div className="flex items-center border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-red-400 transition-colors">
            <i className="ri-lock-line text-gray-400 text-sm"></i>
            <input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="密码" className="flex-1 outline-none text-sm" required />
          </div>
          <button type="submit" disabled={loading} className="w-full py-3.5 bg-red-600 text-white font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap disabled:opacity-60 mt-1">
            {loading ? '处理中...' : mode === 'login' ? '登录工作台' : '申请入驻'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Main Section ─────────────────────────────────────────────────────────────
export default function KaoyanSection() {
  const [showTeacherAuth, setShowTeacherAuth] = useState(false);
  const [showDash, setShowDash] = useState(false);
  const [payingCourse, setPayingCourse] = useState<typeof kaoyanCourses[0] | null>(null);

  const handleTeacherLogin = () => {
    setShowTeacherAuth(false);
    setShowDash(true);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-red-600 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-fire-fill text-white text-sm"></i>
              <span className="text-white text-xs font-bold tracking-wide">考研加油站</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">
              考研加油 · 名师助力
            </h2>
            <p className="text-gray-500 mt-2 text-sm">精选考研数学、英语、政治、专业课名师，助你一次上岸</p>
          </div>
          <button
            onClick={() => setShowTeacherAuth(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-full hover:bg-gray-700 transition-all cursor-pointer whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-user-star-line"></i>教师入驻 / 登录
          </button>
        </div>

        {/* Hero Banner */}
        <div className="relative rounded-3xl overflow-hidden mb-8 h-52 md:h-64">
          <img
            src="https://readdy.ai/api/search-image?query=motivated%20Chinese%20graduate%20students%20studying%20hard%20together%20in%20library%2C%20books%20and%20notes%20everywhere%2C%20determined%20expressions%2C%20warm%20encouraging%20atmosphere%2C%20university%20library%20setting%2C%20inspiring%20academic%20environment%2C%20soft%20warm%20lighting&width=1200&height=400&seq=ky_hero&orientation=landscape"
            alt="考研加油"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/80 via-red-800/60 to-transparent"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-12">
            <p className="text-red-200 text-xs font-semibold tracking-widest uppercase mb-2">Graduate Exam · 全力冲刺</p>
            <h3 className="text-white font-black text-2xl md:text-3xl mb-2">考研路上，我们陪你！</h3>
            <p className="text-white/80 text-sm mb-4 max-w-md">数学·英语·政治·专业课，四科全覆盖，名师一对一答疑，上岸率高达92%</p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1.5">
                <i className="ri-user-smile-line text-white text-xs"></i>
                <span className="text-white text-xs font-semibold">60,000+ 考研学子</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1.5">
                <i className="ri-medal-line text-amber-300 text-xs"></i>
                <span className="text-white text-xs font-semibold">92% 上岸率</span>
              </div>
              <div className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1.5">
                <i className="ri-graduation-cap-line text-white text-xs"></i>
                <span className="text-white text-xs font-semibold">500+ 认证名师</span>
              </div>
            </div>
          </div>
        </div>

        {/* Subject Tags */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { label: '考研数学', icon: 'ri-calculator-line', color: 'bg-red-50 text-red-600 border-red-200' },
            { label: '考研英语', icon: 'ri-translate-2', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
            { label: '考研政治', icon: 'ri-government-line', color: 'bg-amber-50 text-amber-600 border-amber-200' },
            { label: '专业课辅导', icon: 'ri-book-open-line', color: 'bg-orange-50 text-orange-600 border-orange-200' },
            { label: '考研规划', icon: 'ri-calendar-check-line', color: 'bg-rose-50 text-rose-600 border-rose-200' },
            { label: '真题解析', icon: 'ri-file-list-3-line', color: 'bg-gray-50 text-gray-600 border-gray-200' },
          ].map(tag => (
            <span key={tag.label} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${tag.color}`}>
              <i className={tag.icon}></i>{tag.label}
            </span>
          ))}
        </div>

        {/* Course Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {kaoyanCourses.map(course => (
            <div key={course.id} className="bg-[#faf9f7] rounded-2xl overflow-hidden group hover:scale-[1.02] transition-all">
              <div className="relative h-36 overflow-hidden">
                <img src={course.image} alt={course.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full font-medium">{course.subject}</div>
                <div className="absolute bottom-2 left-2 flex items-center gap-1">
                  <i className="ri-star-fill text-amber-400 text-xs"></i>
                  <span className="text-white text-xs font-semibold">{course.rating}</span>
                </div>
              </div>
              <div className="p-4">
                <p className="text-xs text-gray-400 mb-1">{course.teacher}</p>
                <h3 className="font-bold text-gray-900 text-sm leading-snug mb-2 line-clamp-2">{course.title}</h3>
                <div className="flex items-center gap-1 mb-3">
                  <i className="ri-user-line text-gray-300 text-xs"></i>
                  <span className="text-xs text-gray-400">{course.students.toLocaleString()}人已学</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-red-600 font-bold text-base">¥{course.price}</span>
                    <span className="text-gray-300 text-xs line-through ml-1">¥{course.originalPrice}</span>
                  </div>
                  <button
                    onClick={() => setPayingCourse(course)}
                    className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
                  >
                    立即购买
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Teacher CTA */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-red-600/20 rounded-xl flex-shrink-0">
              <i className="ri-user-star-line text-red-400 text-2xl"></i>
            </div>
            <div>
              <p className="text-white font-black text-base">考研名师入驻</p>
              <p className="text-white/60 text-xs mt-0.5">注册账号 · 上传课程 · 查看销售 · 提现到账</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-white/50 text-xs">平台教师平均月收入</p>
              <p className="text-amber-400 font-black text-lg">¥18,500</p>
            </div>
            <button
              onClick={() => setShowTeacherAuth(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-red-600 text-white text-sm font-black rounded-full hover:bg-red-500 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-star-line"></i>立即入驻
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showTeacherAuth && <TeacherAuthModal onSuccess={handleTeacherLogin} />}
      {showDash && <TeacherDashModal onClose={() => setShowDash(false)} />}
      {payingCourse && <PaymentModal course={payingCourse} onClose={() => setPayingCourse(null)} />}
    </section>
  );
}
